import { useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/legacy/image';
import styled, { withTheme, DefaultTheme } from 'styled-components';

import { OFFICES_URL_PREFIX } from '../../common/consts';
import useI18n from '../../common/hooks/useI18n';

type Props = {
  query: string;
  location: string;
  disableImage?: boolean;
  disableHref?: boolean;
  theme: DefaultTheme;
};

const Background = styled.div`
  position: relative;
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media ${(props) => props.theme.breakpoints.smd} {
    height: 160px;
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  width: calc(var(--vw-resizable, 1vw) * 100);
  z-index: -1;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1;
`;

const Title = styled.h3<{ $hasImage: boolean }>`
  ${(props) => props.theme.font.h2};
  color: ${(props) =>
    props.$hasImage ? 'white' : props.theme.colors.secondary};
  ${(props) => (props.$hasImage ? 'margin-bottom: unset;' : '')}
`;

const Divider = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  border-top: 1px solid #e0e0e0;
`;

const LocationTitle: React.FC<Props> = ({
  query,
  location,
  disableImage = false,
  disableHref = false,
  theme,
}) => {
  const i18n = useI18n();

  const title = useMemo(
    () =>
      i18n.t('header.offices.officesInCity', {
        city:
          i18n.t(`dynamic.officesByCountyPage.${query}.titleShort`) || location,
      }),
    [i18n, query, location]
  );
  const imageSrc = useMemo(
    () => i18n.stagingT(`dynamic.officesByCountyPage.${query}.heroImageUrl`),
    [query, i18n]
  );
  const hasImage = useMemo(() => !disableImage && !!imageSrc, [
    disableImage,
    imageSrc,
  ]);
  const titleElement = useMemo(() => {
    if (disableHref) {
      return (
        <Title as="h3" $hasImage={hasImage}>
          {title}
        </Title>
      );
    }

    return (
      <Link href={`/${OFFICES_URL_PREFIX}/${query}`} passHref legacyBehavior>
        <a>
          <Title as="h3" $hasImage={hasImage}>
            {title}
          </Title>
        </a>
      </Link>
    );
  }, [query, disableHref, hasImage, title]);

  if (!hasImage) {
    return (
      <>
        {titleElement}
        <Divider />
      </>
    );
  }

  return (
    <Background>
      <ImageAbsoluteContainer>
        <ImageContainer>
          <Image
            alt={title}
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            src={imageSrc}
            sizes={theme.getImageSizes('100vw')}
          />
        </ImageContainer>

        <ImageOverlay />
      </ImageAbsoluteContainer>

      {titleElement}
    </Background>
  );
};

export default withTheme<typeof LocationTitle>(LocationTitle);
